import React, { useState } from 'react';

import { APIProviderIntegrationStatus } from '@cd3p/core/types/api';
import { ConfirmationDialog, FormField } from 'components';

import { IntegrationClearButton } from './IntegrationClearButton';

import { Control, useTranslation } from 'third-party';

type IntegrationServiceFieldProps = {
  fieldName: string;
  placeholder: string;
  label: string;
  control: Control<any, any>;
  integrationServiceOptions: React.ReactNode;
  isIntegrationSelected: boolean;
  integrationStatus: APIProviderIntegrationStatus | null;
  onChangeConfirm: () => void;
  onChangeCancel: () => void;
  onClearIntegrationConfirm: () => Promise<void>;
};

enum DialogType {
  Clear = 'clear',
  Change = 'change',
}

export const IntegrationServiceField: React.FC<
  IntegrationServiceFieldProps
> = ({
  fieldName,
  placeholder,
  label,
  control,
  integrationServiceOptions,
  isIntegrationSelected,
  integrationStatus,
  onChangeConfirm,
  onChangeCancel,
  onClearIntegrationConfirm,
}) => {
  const { t } = useTranslation();
  const [dialogType, setDialogType] = useState<DialogType | null>(null);

  const openDialog = (type: DialogType) => setDialogType(type);
  const closeDialog = () => setDialogType(null);

  const integrationNotStarted =
    !integrationStatus ||
    integrationStatus === APIProviderIntegrationStatus.NotStarted;

  const handleChangeRequest = () => {
    integrationNotStarted ? onChangeConfirm() : openDialog(DialogType.Change);
  };

  const handleClearRequest = () => {
    integrationNotStarted
      ? onClearIntegrationConfirm()
      : openDialog(DialogType.Clear);
  };

  const handleActionClick = async () => {
    if (dialogType === DialogType.Clear) {
      await onClearIntegrationConfirm();
    } else if (dialogType === DialogType.Change) {
      onChangeConfirm();
    }

    closeDialog();
  };

  const handleCancelClick = () => {
    onChangeCancel();
    closeDialog();
  };

  return (
    <>
      <FormField
        isRequired={false}
        fieldName={fieldName}
        label={label}
        selectOptions={integrationServiceOptions}
        placeholder={placeholder}
        onChange={handleChangeRequest}
        InputProps={{
          endAdornment: (
            <IntegrationClearButton
              disabled={!isIntegrationSelected}
              onClearClick={handleClearRequest}
            />
          ),
        }}
        control={control}
      />
      <ConfirmationDialog
        onClose={() => setDialogType(null)}
        open={!!dialogType}
        handleCancelClick={handleCancelClick}
        handleActionClick={handleActionClick}
        description={
          dialogType === 'clear'
            ? t('settings.integrations.form.areYouSure')
            : t('settings.integrations.form.areYouSureChangeIntegration')
        }
        cancelButtonTitle={t('common.cancel')}
        actionButtonTitle={
          dialogType === 'clear'
            ? t('common.confirmDelete')
            : t('common.yes.change')
        }
      />
    </>
  );
};
